import {
  Button,
  FormLabel,
  Icon,
  LoadingSpinner,
  Radio,
  RadioList,
  Select,
  Toast,
  Tooltip,
  useToast,
} from "@oysterhr/ui"
import { MoneyInput, CountrySelect } from "@oysterhr/feature-components"
import { Controller, FormProvider, useForm } from "react-hook-form"
import { Trans, useTranslation } from "react-i18next"
import { amplitudeLogEvent } from "~/app/utils/amplitudeHelpers"
import { useEffect } from "react"
import { booleanToTextValue, textValueToBoolean } from "~/app/utils/helpers"
import { isEqual } from "lodash-es"
import {
  Maybe,
  SalaryFrequencyEnumType,
  SelectedSalaryEnumType,
  VatParameters,
} from "@oysterhr/schema"
import { allCurrencies } from "@oysterhr/common"
import { CalculationFormValues } from "../../types"
import { CalculationVATForm } from "../CalculationVATForm"
import {
  getOriginalAmountByFrequency,
  getTotalAmountByFrequency,
  hasStateOrProvince,
  validateSalary,
} from "../../utils/helpers"
import { useCalculationsProvider } from "../../contexts"
import {
  useCalculationsParams,
  useLazyBulkSalaryCalculation,
  useVatOptionsQuery,
} from "../../hooks"

const SALARY_FREQUENCY_OPTIONS = [
  { label: "Annually", value: SalaryFrequencyEnumType.Annual },
  { label: "Monthly", value: SalaryFrequencyEnumType.Monthly },
]

export const SALARY_TYPE_OPTIONS = [
  { label: "Gross salary", value: SelectedSalaryEnumType.Gross },
  { label: "Net salary", value: SelectedSalaryEnumType.Net },
]

type SelectOption = { label: string; value: string }

interface CalculationFormProps {
  toggleForm: (isOpen: boolean) => void
}

export const CalculationForm = ({ toggleForm }: CalculationFormProps) => {
  // Hooks
  const { t } = useTranslation("CostCalculator")
  const [calculateSalary, { loading }] = useLazyBulkSalaryCalculation()
  const { getParam } = useCalculationsParams()
  const toast = useToast()

  /* If a single country is present in URL, prefill the country select
   * the use case for this is when users are accessing the hiring page
   * of a specific country in marketing website.
   */
  const countryParam = getParam("countryCode")?.split(",")
  const prefilledCountryValue = countryParam?.length === 1 ? countryParam[0] : ""

  // Providers
  const {
    addCalculation,
    calculations,
    multiSelectOptions,
    eventPrefix,
    editCalculation,
    activeCalculation,
    activeCalculationIndex,
    isEditMode,
    setIsEditMode,
  } = useCalculationsProvider()

  const countryData = multiSelectOptions?.options || []

  // Form
  const form = useForm<CalculationFormValues>({
    defaultValues: {
      countryCode: prefilledCountryValue,
      stateName: undefined,
      hasStateOrProvince: false,
      salary: {
        currency: activeCalculation?.currency.code ?? "EUR",
        amount: "",
      },
      serviceWithinCountry: "",
      salesWithinCountry: "",
      salesPercentage: "",
      salaryFrequency: SalaryFrequencyEnumType.Annual,
      salaryType: SelectedSalaryEnumType.Gross,
    },
  })
  const { setValue, clearErrors, handleSubmit, watch, register, control } = form

  // Side effect
  useEffect(() => {
    if (isEditMode) {
      const countryCode = activeCalculation?.country.code || ""
      const hasState = hasStateOrProvince(countryCode || "", multiSelectOptions)
      const formSalaryType = watch("salaryType")
      const selectedSalary = activeCalculation?.selectedSalary || formSalaryType
      const salaryFrequency = activeCalculation?.salaryFrequency || SalaryFrequencyEnumType.Annual

      const salaryAmount =
        selectedSalary === SelectedSalaryEnumType.Gross
          ? activeCalculation?.annualGrossSalary.toString()
          : activeCalculation?.totals.netSalary.toString()

      // Fill active calculation values if edit mode is enabled
      setValue("countryCode", countryCode)
      setValue("stateName", activeCalculation?.stateOrProvince || "")
      setValue("hasStateOrProvince", hasState)
      setValue("salary", {
        amount: salaryAmount
          ? getOriginalAmountByFrequency(parseFloat(salaryAmount), salaryFrequency).toString()
          : "",
        currency: activeCalculation?.currency.code || "EUR",
      })
      setValue("salaryType", selectedSalary)
      setValue("salaryFrequency", salaryFrequency)

      const worksInCountryOfResidence = activeCalculation?.vatParameters?.worksInCountryOfResidence
      const salesToCountry = activeCalculation?.vatParameters?.salesToCountry
      const salesPercentageToCountry = activeCalculation?.vatParameters?.salesPercentageToCountry

      setValue(
        "serviceWithinCountry",
        worksInCountryOfResidence != null ? booleanToTextValue(worksInCountryOfResidence) : "",
      )
      setValue(
        "salesWithinCountry",
        salesToCountry != null ? booleanToTextValue(salesToCountry) : "",
      )
      setValue("salesPercentage", salesPercentageToCountry?.toString() || "")
    }
  }, [isEditMode, activeCalculation, multiSelectOptions, setValue, watch])

  const regionInfo = watch(["countryCode", "stateName", "hasStateOrProvince"])
  const [selectedCountry, _state, hasState] = regionInfo
  const { data } = useVatOptionsQuery({ variables: { countryCode: selectedCountry } })

  const STATE_OPTIONS: SelectOption[] =
    countryData
      .find(option => option.code === selectedCountry)
      ?.stateOrProvinces.map(state => ({ label: state, value: state })) || []

  const clearVATValues = () => {
    setValue("serviceWithinCountry", "")
    setValue("salesWithinCountry", "")
    setValue("salesPercentage", "")
  }

  const handleCountryChange = (countryCode: string | null | undefined) => {
    const [previousCountry] = regionInfo

    setValue("countryCode", countryCode || "")

    const hasState = hasStateOrProvince(countryCode || "", multiSelectOptions)
    setValue("hasStateOrProvince", hasState)

    if (countryCode) {
      clearErrors("countryCode")

      // Clear state and VAT info if a different country is selected
      if (countryCode !== previousCountry) {
        setValue("stateName", "")
        clearVATValues()
      }
    }
  }

  const onSubmit = async (values: CalculationFormValues) => {
    if (isEditMode) return handleEdit(values)

    // First calculation event name
    let eventName = `${eventPrefix}-submit_cost_calculator-customer_onboarding`
    // Different event if not first calculation
    if (calculations.length > 0) {
      eventName = `${eventPrefix}-add_another_calculation-customer-onboarding`
    }

    const calculationQuery = {
      annualGrossSalary:
        values.salaryType === SelectedSalaryEnumType.Gross
          ? getTotalAmountByFrequency(parseFloat(values.salary.amount), values.salaryFrequency)
          : undefined,
      annualNetSalary:
        values.salaryType === SelectedSalaryEnumType.Net
          ? getTotalAmountByFrequency(parseFloat(values.salary.amount), values.salaryFrequency)
          : undefined,
      countryCode: values.countryCode,
      stateOrProvince: values.stateName || undefined,
      currencyCode: values.salary.currency,
      nativeCurrencyCode: values.salary.currency,
      vatParameters: {
        worksInCountryOfResidence: values.serviceWithinCountry
          ? textValueToBoolean(values.serviceWithinCountry)
          : undefined,
        salesToCountry: values.salesWithinCountry
          ? textValueToBoolean(values.salesWithinCountry)
          : undefined,
        salesPercentageToCountry: values.salesPercentage
          ? Number(values.salesPercentage)
          : undefined,
      },
      selectedSalary: values.salaryType,
      salaryFrequency: values.salaryFrequency,
    }

    amplitudeLogEvent(eventName, calculationQuery)

    const response = await calculateSalary({
      variables: {
        calculationQueries: [calculationQuery],
      },
    })

    const calculationsResponse = response.data?.bulkSalaryCalculations

    if (calculationsResponse?.length) {
      const updatedCalculation = calculationsResponse[0]

      addCalculation(updatedCalculation)
    }

    clearVATValues()
    toggleForm(false)
  }

  const checkVATUpdates = (
    updatedValues: Maybe<VatParameters> | undefined,
    previousValues: Maybe<VatParameters> | undefined,
  ) => {
    const hasChanged = !isEqual(updatedValues, previousValues)
    if (hasChanged) {
      toast({
        render: (
          <Toast variation="success">
            <Toast.Content>{t("flashMessage.vatUpdate.success")}</Toast.Content>
          </Toast>
        ),
      })
    }
  }

  const handleEdit = async (values: CalculationFormValues) => {
    // Previous calculation values must always be present in edit mode
    if (!activeCalculation) return

    const previousSalaryType = activeCalculation.selectedSalary || SelectedSalaryEnumType.Gross
    const previousSalaryFrequency =
      activeCalculation.salaryFrequency || SalaryFrequencyEnumType.Annual
    const previousGrossSalaryAmount = getOriginalAmountByFrequency(
      activeCalculation.annualGrossSalary,
      previousSalaryFrequency,
    ).toString()
    const previousNetSalaryAmount = getOriginalAmountByFrequency(
      activeCalculation.totals.netSalary,
      previousSalaryFrequency,
    ).toString()

    const hasVat = activeCalculation?.vatOptions
    const hasCountryChanged = values.countryCode !== activeCalculation?.country.code
    const hasSalaryTypeChanged = values.salaryType !== activeCalculation?.selectedSalary
    const hasSalaryFrequencyChanged = values.salaryFrequency !== activeCalculation?.salaryFrequency
    const hasSalaryValueChanged =
      values.salary.amount !==
      (previousSalaryType === SelectedSalaryEnumType.Gross
        ? previousGrossSalaryAmount
        : previousNetSalaryAmount)

    const hasSalaryInfoChanged =
      hasSalaryTypeChanged || hasSalaryFrequencyChanged || hasSalaryValueChanged

    const useNetSalary = values.salaryType === SelectedSalaryEnumType.Net

    const newGrossSalaryAmount = hasSalaryInfoChanged
      ? getTotalAmountByFrequency(parseFloat(values.salary.amount), values.salaryFrequency)
      : activeCalculation.annualGrossSalary

    const vatData = {
      worksInCountryOfResidence: values.serviceWithinCountry
        ? textValueToBoolean(values.serviceWithinCountry)
        : activeCalculation?.vatParameters?.worksInCountryOfResidence,
      salesToCountry:
        values.salesWithinCountry !== activeCalculation?.vatParameters?.salesToCountry
          ? textValueToBoolean(values.salesWithinCountry)
          : activeCalculation?.vatParameters?.salesToCountry,
      salesPercentageToCountry:
        values.salesPercentage || activeCalculation?.vatParameters?.salesPercentageToCountry,
    }

    const calculationQuery = {
      annualGrossSalary: !useNetSalary ? newGrossSalaryAmount : undefined,
      annualNetSalary: useNetSalary
        ? getTotalAmountByFrequency(parseFloat(values.salary.amount), values.salaryFrequency)
        : undefined,
      selectedSalary: values.salaryType,
      salaryFrequency: values.salaryFrequency,
      countryCode: values.countryCode,
      nativeCurrencyCode: values.salary.currency,
      stateOrProvince: values.stateName || undefined,
      currencyCode: values.salary.currency,
      vatParameters: hasCountryChanged && !hasVat ? undefined : vatData,
    }

    const previousValues: Maybe<VatParameters> | undefined = activeCalculation?.vatParameters
      ? { ...activeCalculation?.vatParameters }
      : null

    try {
      const response = await calculateSalary({
        variables: {
          calculationQueries: [calculationQuery],
        },
      })

      const calculationsResponse = response.data?.bulkSalaryCalculations

      if (calculationsResponse?.length) {
        const updatedCalculation = calculationsResponse[0]
        editCalculation(updatedCalculation, activeCalculationIndex)
        checkVATUpdates(updatedCalculation.vatParameters, previousValues)
      }
    } catch {
      toast({
        render: (
          <Toast variation="error">
            <Toast.Content>{t("flashMessage.vatUpdate.error")}</Toast.Content>
          </Toast>
        ),
      })
    }

    setIsEditMode(false)
  }

  const validateStateOrProvince = (stateOrProvince: string) => {
    if (hasStateOrProvince(selectedCountry, multiSelectOptions))
      return !stateOrProvince ? t("requiredState") : true

    return true
  }

  return (
    <FormProvider {...form}>
      <div className="w-full">
        <form
          className="flex w-full flex-col items-center"
          onSubmit={handleSubmit(onSubmit)}
          data-testid="calculation-form"
        >
          <div className="flex max-w-sm flex-col space-y-4">
            <div className="flex flex-col gap-1.5">
              <Controller
                name="countryCode"
                rules={{ required: t("requiredCountry").toString() }}
                control={control}
                render={({ field: { value, name }, fieldState: { error } }) => (
                  <CountrySelect
                    label={<FormLabel label={t("labelCountry")} />}
                    countries={countryData.map(country => ({
                      code: country.code,
                      name: country.name,
                    }))}
                    placeholder={t("country")}
                    onChange={handleCountryChange}
                    value={value}
                    name={name}
                    error={error?.message}
                  />
                )}
              />
            </div>
            {hasState && (
              <div className="flex flex-col gap-1.5">
                <FormLabel label={t("stateOrProvince")} />
                <Controller
                  name="stateName"
                  control={control}
                  rules={{ validate: validateStateOrProvince }}
                  render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                    <Select
                      placeholder={t("stateOrProvince")}
                      value={value}
                      name={name}
                      onChange={onChange}
                      error={error?.message}
                    >
                      {STATE_OPTIONS.map(option => (
                        <Select.Option value={option.value} key={option.value}>
                          {option.label}
                        </Select.Option>
                      ))}
                    </Select>
                  )}
                />
              </div>
            )}
            <div className="flex flex-col items-start justify-center md:flex-row md:space-x-4">
              <Controller
                control={form.control}
                name="salary"
                rules={{
                  required: true,
                  validate: input => validateSalary(input, t("requiredSalary")),
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => {
                  return (
                    <MoneyInput
                      currencies={allCurrencies}
                      label={<FormLabel label={t("labelSalary")} />}
                      error={error?.message}
                      value={value}
                      onChange={onChange}
                    />
                  )
                }}
              />
              <Controller
                name="salaryFrequency"
                control={control}
                render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                  // This is to fix the alignment of money input and new cycle field regardless of an error
                  // Will look for a better solution when start working on the form in another PR
                  <div className="w-full flex-col pt-3.5 md:mt-4">
                    <Select
                      size="md"
                      placeholder={t("annually")}
                      onChange={onChange}
                      value={value}
                      name={name}
                      id="salaryFrequency"
                      error={error?.message}
                    >
                      {SALARY_FREQUENCY_OPTIONS.map(option => (
                        <Select.Option value={option.value} key={option.value}>
                          {option.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                )}
              />
            </div>
            <div className="flex flex-col">
              <RadioList
                orientation="horizontal"
                label={
                  <FormLabel
                    label={t("labelSalaryType")}
                    endAdornment={
                      <Tooltip
                        value={
                          <Trans
                            defaults={t("salaryTypeTooltip")}
                            components={{
                              br: <br />,
                            }}
                          />
                        }
                      >
                        <Icon name="Question" size="sm" />
                      </Tooltip>
                    }
                  />
                }
              >
                {SALARY_TYPE_OPTIONS.map(salaryType => (
                  <Radio
                    {...register("salaryType", { required: true })}
                    key={salaryType.value}
                    value={salaryType.value}
                    id={salaryType.value}
                    label={salaryType.label}
                    data-testid={`radio-${salaryType.value}`}
                  />
                ))}
              </RadioList>
            </div>
            {data?.vatOptions && (
              <CalculationVATForm vatOptions={data.vatOptions} countryCode={selectedCountry} />
            )}
            <div className="w-full">
              <Button
                isFullWidth={true}
                type="submit"
                data-analytics-name={
                  calculations.length > 0
                    ? "cost-calculator-add-another"
                    : "cost-calculator-calculate"
                }
              >
                {loading && <LoadingSpinner />}
                {isEditMode
                  ? t("recalculate")
                  : calculations.length === 0
                    ? t("labelSubmit")
                    : t("labelAddAnother")}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </FormProvider>
  )
}
